import { useState, useEffect, useCallback, useRef } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import CalendarView from "./pages/calendar/calendar-view"
import ServicesView from "./pages/servicios/services-view"
import AdminDashboard from "./pages/admin-dashboard/admin-dashboard"
import Footer from "./components/footer"
import BrandModal from "./components/BrandModal"
import { Scissors, Shield, ClipboardList, Store, ShoppingBag, Home as HomeIcon, Star, Heart, Crown, Zap, Coffee, Stethoscope, Megaphone } from "lucide-react"
import { SpinnerCarga } from "./components/SpinnerCarga"
import ClientServiceP from "./services/client/ClientPService"
import alerts from "./components/alertas"
import AdminLogin from "./pages/Login/AdminLogin"
import ClientService from "./services/client/ClientService"
import logger from "./utils/logger";
import "./pages/admin-dashboard/panel.css";
import "./pages/public/public.css";
import { getSetupStatus } from "./pages/admin-dashboard/setupSteps";

function Home({ clientSlug: propSlug }) {
  const { clientSlug: paramSlug } = useParams()
  const clientSlug = propSlug || paramSlug
  const location = useLocation();
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [clientData, setClientData] = useState({
    business: { name: 'Turnlink', logoUrl: null },
  })
  const [requiredTab, setRequiredTab] = useState(null);
  const [completedSteps, setCompletedSteps] = useState(0);
  const [totalSteps, setTotalSteps] = useState(4);
  const [onboardingMessage, setOnboardingMessage] = useState("");
  const [currentView, setCurrentView] = useState("services")
  const [preloadedImages, setPreloadedImages] = useState({})
  const [imagesLoaded, setImagesLoaded] = useState(false)
  const [customColors, setCustomColors] = useState({})
  const [selectedService, setSelectedService] = useState(null);
  const [adminLogged, setAdminLogged] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const lastRequiredTab = useRef(null);
  const [setupJustCompleted, setSetupJustCompleted] = useState(false); // para abrir "Así funciona" una vez al terminar los 4 pasos
  const [loading, setLoading] = useState(true);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const announcementShownRef = useRef(false);
  const navigate = useNavigate();

  // Aviso del negocio: se muestra UNA vez por carga de página cuando está activo y
  // tiene mensaje. El cliente lo cierra y sigue agendando (ref evita re-abrirlo si
  // clientData se refresca).
  useEffect(() => {
    const a = clientData?.personalization?.announcement;
    if (!announcementShownRef.current && a?.enabled && a?.message) {
      announcementShownRef.current = true;
      setShowAnnouncement(true);
    }
  }, [clientData]);

  useEffect(() => {
    document.getElementById('initial-loader')?.remove();
    
    // Agregar clase al body para ocultar footer estático
    document.body.classList.add('js-loaded');
    
    const timer = setTimeout(() => setTimeoutReached(true), 10000); // 10 segundos
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const googleCalendar = params.get("googleCalendar");
    if (
      clientSlug &&
      (tab === "connections" || googleCalendar === "connected")
    ) {
      setCurrentView("admin");
      navigate(`/${clientSlug}`, { replace: true });
    }
  }, [clientSlug, location.search, navigate]);

  // Estado para la configuración de marca
  const [brandSettings, setBrandSettings] = useState({
    logoType: 'icon',
    selectedIcon: 'Scissors',
    logoImage: null,
    businessName: 'Turnlink'
  })

  // Mapeo de iconos disponibles
  const iconMap = {
    Scissors,
    Store,
    ShoppingBag,
    Home: HomeIcon,
    Star,
    Heart,
    Crown,
    Zap,
    Coffee,
    Stethoscope
  }

  const preloadImagesAsBase64 = useCallback(async (clientSlug) => {
    if (imagesLoaded) return
    try {
      const allImagesData = await ClientServiceP.getAllServiceImages(clientSlug)
      const newPreloaded = {}
      
      // Simplemente organizar las URLs por servicio
      allImagesData.services.forEach((service) => {
        newPreloaded[service.serviceIndex] = service.images.map(img => img.url)
      })
      
      setPreloadedImages(newPreloaded)
      setImagesLoaded(true)
    } catch (error) {
      logger.error("Error preloading images:", error)
      setImagesLoaded(true)
    }
  }, [imagesLoaded])

  useEffect(() => {
    if (clientSlug && !imagesLoaded) {
      preloadImagesAsBase64(clientSlug)
    }
  }, [clientSlug, imagesLoaded, preloadImagesAsBase64])

  // Primer paso de configuración pendiente (o null). Los pasos viven en setupSteps.js,
  // compartidos con la sección "Primeros pasos"; en la vista pública no se calculan.
  const checkConfiguration = (data) => {
    if (!localStorage.getItem('clientId')) return null;
    const status = getSetupStatus(data);
    if (!status.current) return null;
    return {
      requiredTab: status.current.key,
      message: `Paso ${status.currentIndex + 1}/${status.total}: ${status.current.label}`,
      stepNumber: status.currentIndex + 1,
      totalSteps: status.total,
      completedSteps: status.completed,
    };
  };

  const getClientData = useCallback(async (slug) => {
    setLoading(true);  // Marca como cargando al inicio
    let data;
    try {
      const storedClientId = localStorage.getItem('clientId');
      const storedToken = localStorage.getItem('turnlinkToken');

      let fetchedData;
      if (storedClientId && storedToken) {
        try {
          fetchedData = await ClientService.getClientDataById(storedClientId);

          // Validar que el slug coincida
          if (fetchedData.slug !== slug) {
            localStorage.removeItem('clientId');
            localStorage.removeItem('turnlinkToken');
            setAdminLogged(false);

            // Obtener datos públicos del nuevo slug
            fetchedData = await ClientServiceP.getClientBySlug(slug);
          }
        } catch (error) {
          logger.error("Error al obtener datos autenticados:", error);

          // Si falla la llamada autenticada (token inválido), limpiar y obtener datos públicos
          localStorage.removeItem('clientId');
          localStorage.removeItem('turnlinkToken');
          setAdminLogged(false);

          fetchedData = await ClientServiceP.getClientBySlug(slug);
        }
      } else {
        // Sin autenticación, obtener datos públicos
        fetchedData = await ClientServiceP.getClientBySlug(slug);
      }
      data = fetchedData;
      setClientData(data);
      setBrandSettings({
        logoType: data.personalization?.logoType || 'icon',
        selectedIcon: data.personalization?.selectedIcon || 'Scissors',
        logoImage: data.personalization?.logoImage || null,
        businessName: data.business?.name || 'TurnLink'
      });
      setCustomColors({
        primaryColor: data.personalization?.primaryColor,
        primaryDark: data.personalization?.primaryDark,
      });
      const config = checkConfiguration(data);
      const isLogged = !!localStorage.getItem('turnlinkToken');
      if (!data.configComplete) {
        if (isLogged && config) {
          // Solo calcular pasos si está logueado
          setRequiredTab(config.requiredTab);
          setOnboardingMessage(config.message);
          setCompletedSteps(config.completedSteps);
          setTotalSteps(config.totalSteps);
        } else {
          // Para público, no calcular pasos, solo mostrar login
          setRequiredTab(null);
          setOnboardingMessage("");
          setCompletedSteps(0);
          setTotalSteps(4);
        }
        setCurrentView("admin");
      } else {
        setRequiredTab(null);
        setOnboardingMessage("");
        setCompletedSteps(4);
        setTotalSteps(4);
        setCurrentView(isLogged ? "admin" : "services");
      }
    } catch (error) {
      logger.error("Error loading client data:", error);
    } finally {
      setLoading(false);  // Marca como cargado al final
    }
    return data;
  }, []);

  // Efecto para cargar datos del cliente
  useEffect(() => {
    if (clientSlug) {
      getClientData(clientSlug);
    } else {
      const savedBrand = localStorage.getItem('brandSettings');
      if (savedBrand) {
        setBrandSettings(JSON.parse(savedBrand));
      }
    }
  }, [clientSlug, getClientData]);

  useEffect(() => {
    if (!customColors.primaryColor || !customColors.primaryDark) return;
    updateColors(customColors);
  }, [customColors]);

  const handleAdminLogin = async (username, password) => {
    setLoginLoading(true);
    try {
      const response = await ClientServiceP.loginAdmin(username, password, clientSlug);
      if (!response.clientId || !response.access_token) {
        setLoginLoading(false);
        return;
      }
      // Guardar credenciales
      localStorage.setItem('clientId', response.clientId);
      localStorage.setItem('turnlinkToken', response.access_token);
      const userData = {
        username: response.username,
        userType: response.userType,
      };
      localStorage.setItem('userData', JSON.stringify(userData));
      // Actualizar estado
      setAdminLogged(true);
      await getClientData(clientSlug);
    } catch (error) {
      // Limpiar almacenamiento en caso de error
      localStorage.removeItem('clientId');
      localStorage.removeItem('turnlinkToken');
      setAdminLogged(false);
      if (error.response?.status === 401) {
        alerts.showError("Usuario o contraseña incorrectos");
      } else {
        alerts.showError("Error al iniciar sesión: " + (error.message || "Intenta nuevamente"));
      }
    } finally {
      setLoginLoading(false);
    }
  }

  useEffect(() => {
    const token = localStorage.getItem('turnlinkToken');
    const clientId = localStorage.getItem('clientId');

    // Solo marcar como no logueado si AMBOS faltan
    if (!token || !clientId) {
      setAdminLogged(false);
    } else {
      setAdminLogged(true);
    }
  }, []);

  // Función para actualizar colores dinámicamente
  const updateColors = (newColors) => {
    const root = document.documentElement

    // Actualizar variables CSS del global.css
    root.style.setProperty('--primary-color', newColors.primaryColor)
    root.style.setProperty('--primary-dark', newColors.primaryDark)

    // Convertir color hex a rgba para las sombras
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }

    // Actualizar variables de sombra dinámicas
    root.style.setProperty('--primary-shadow', hexToRgba(newColors.primaryColor, 0.3))
    root.style.setProperty('--primary-shadow-hover', hexToRgba(newColors.primaryColor, 0.4))

    // Actualizar la clase .text-primary del index.css
    let textPrimaryStyle = document.getElementById('dynamic-text-primary')
    if (!textPrimaryStyle) {
      textPrimaryStyle = document.createElement('style')
      textPrimaryStyle.id = 'dynamic-text-primary'
      document.head.appendChild(textPrimaryStyle)
    }

    textPrimaryStyle.textContent = `
      .text-primary {
        color: ${newColors.primaryColor} !important;
      }
    `
  }

  // Ícono de marca para la barra del panel (logo subido o ícono elegido).
  const BrandIconComponent = iconMap[brandSettings.selectedIcon] || Scissors;
  const brandIcon = brandSettings.logoType === 'image' && brandSettings.logoImage
    ? <img src={brandSettings.logoImage} alt="" />
    : <BrandIconComponent size={24} />;

  const adminDashboard = (
    <AdminDashboard
      preloadedImages={preloadedImages}
      requiredTab={requiredTab}
      onboardingMessage={onboardingMessage}
      onConfigComplete={async () => {

        // Solo recarga si hay un paso pendiente Y el paso requerido cambió
        if (requiredTab && lastRequiredTab.current !== requiredTab) {
          lastRequiredTab.current = requiredTab;
          const updatedData = await getClientData(clientSlug);
          const config = checkConfiguration(updatedData);
          if (config) {
            setRequiredTab(config.requiredTab);
            setOnboardingMessage(config.message);
            setCompletedSteps(config.completedSteps);
            setTotalSteps(config.totalSteps);
          } else {
            // TODOS LOS PASOS COMPLETADOS
            setRequiredTab(null);
            setOnboardingMessage("¡Configuración completa! 🎉");
            setCompletedSteps(4);
            setTotalSteps(4);
            setSetupJustCompleted(true);
            lastRequiredTab.current = null;
            const updatedData = await getClientData(clientSlug);
            setClientData(updatedData);
          }
        }
      }}
      completedSteps={completedSteps}
      totalSteps={totalSteps}
      brand={{ icon: brandIcon, name: brandSettings.businessName }}
      openGuide={setupJustCompleted}
      onViewAgenda={clientData.configComplete ? () => setCurrentView("services") : undefined}
    />
  );

  if (loading) {
    if (timeoutReached) {
      return (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
          <SpinnerCarga />
          <div className="mt-4 text-muted">La conexión es muy lenta. Intenta recargar la página.</div>
        </div>
      );
    }
    return <SpinnerCarga />;
  }


  // Panel del negocio con sesión: trae su propio shell (barra superior, sidebar /
  // barra inferior). La nav pública y el footer son de la página de reserva.
  if (currentView === "admin" && adminLogged) {
    return (
      <>
        {adminDashboard}
        <SpinnerCarga />
      </>
    );
  }

  // Página pública del negocio (agenda) y login: mismo sistema visual que el panel,
  // con el color de marca como acento. El botón "Admin" se queda arriba a propósito.
  return (
    <div className="cp-root cp-public">
      {showAnnouncement && currentView !== "admin" && (
        <BrandModal
          open
          onClose={() => setShowAnnouncement(false)}
          eyebrow="Aviso"
          icon={<Megaphone size={26} />}
          title={clientData?.personalization?.announcement?.title || "Tenemos un aviso para ti"}
        >
          {clientData?.personalization?.announcement?.message}
        </BrandModal>
      )}

      <header className="cp-topbar" translate="no">
        <div className="cp-topbar-inner">
          <div className="cp-brand">
            <span className="cp-brand-icon">{brandIcon}</span>
            <span className="cp-brand-name">{brandSettings.businessName}</span>
          </div>
          <div className="cp-topbar-actions">
            {clientData.configComplete && (
              <button type="button" className={`cp-btn ${currentView !== "admin" ? "cp-btn-primary" : ""}`} onClick={() => setCurrentView("services")} aria-label="Servicios" title="Servicios">
                <ClipboardList size={16} /> <span className="cp-btn-label">Servicios</span>
              </button>
            )}
            <button type="button" className={`cp-btn ${currentView === "admin" ? "cp-btn-primary" : ""}`} onClick={() => setCurrentView("admin")} aria-label="Admin" title="Entrar al panel">
              <Shield size={16} /> <span className="cp-btn-label">Admin</span>
            </button>
          </div>
        </div>
      </header>

      <main className="cp-public-main">
        {currentView === "calendar" && <CalendarView setCurrentView={setCurrentView} selectedService={selectedService} clientData={clientData} />}
        {currentView === "services" && <ServicesView setCurrentView={setCurrentView} clientData={clientData} preloadedImages={preloadedImages} setSelectedService={setSelectedService} />}
        {currentView === "admin" && (
          !adminLogged
            ? <AdminLogin onLogin={handleAdminLogin} loading={loginLoading} />
            : adminDashboard
        )}
      </main>

      <Footer clientData={clientData} />
      <SpinnerCarga />
    </div>
  )
}

export default Home;
